import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function TeamManagement() {
  const [teams, setTeams]     = useState([]);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading]   = useState(true);
  const [form, setForm] = useState({ name: '', description: '', avatar_color: '#5865f2' });

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/tenant/teams'); setTeams(r.data); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setLoading(false); }
  };

  const loadTeam = async (id) => {
    try { const r = await api.get(`/tenant/teams/${id}`); setSelected(r.data); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  useEffect(() => { load(); }, []);

  const createTeam = async () => {
    if (!form.name.trim()) { toast.error('Team name required'); return; }
    try { await api.post('/tenant/teams', form); toast.success('Team created'); setCreating(false); setForm({ name: '', description: '', avatar_color: '#5865f2' }); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const deleteTeam = async (id) => {
    if (!window.confirm('Deactivate this team?')) return;
    try { await api.delete(`/tenant/teams/${id}`); toast.success('Team deactivated'); setSelected(null); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const removeMember = async (teamId, userId) => {
    try { await api.delete(`/tenant/teams/${teamId}/members/${userId}`); loadTeam(teamId); toast.success('Member removed'); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const COLORS = ['#5865f2','#7c3aed','#ec4899','#f38ba8','#fab387','#a6e3a1','#89b4fa','#94e2d5'];
  const S = {
    card:  { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 16, cursor: 'pointer', marginBottom: 8 },
    avatar: (color) => ({ width: 36, height: 36, borderRadius: 9, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#fff', fontSize: 14, flexShrink: 0 }),
  };

  if (selected) return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button onClick={() => setSelected(null)} className="btn btn-ghost" style={{ fontSize: 12 }}>← Back</button>
        <div style={S.avatar(selected.avatar_color)}>{selected.name?.[0]}</div>
        <h2 style={{ fontSize: 16, fontWeight: 600 }}>{selected.name}</h2>
        <div style={{ flex: 1 }} />
        <button onClick={() => deleteTeam(selected.id)} className="btn btn-ghost" style={{ fontSize: 12, color: '#f38ba8' }}>Deactivate</button>
      </div>
      <div style={{ ...S.card, cursor: 'default' }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Members ({selected.members?.length || 0})</h3>
        {(selected.members || []).length === 0
          ? <p style={{ color: 'var(--text-3)', fontSize: 13 }}>No members yet.</p>
          : (selected.members || []).map(m => (
            <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{m.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{m.email} · {m.role}</div>
              </div>
              <button onClick={() => removeMember(selected.id, m.id)} className="btn btn-ghost" style={{ fontSize: 11, color: '#f38ba8' }}>Remove</button>
            </div>
          ))}
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600 }}>Teams ({teams.length})</h2>
        <button onClick={() => setCreating(!creating)} className="btn btn-primary">＋ New Team</button>
      </div>

      {creating && (
        <div style={{ ...S.card, cursor: 'default', marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Create Team</h3>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Team name *" className="input" style={{ width: '100%', marginBottom: 10 }} />
          <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description" className="input" style={{ width: '100%', marginBottom: 10 }} />
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            {COLORS.map(c => (
              <div key={c} onClick={() => setForm(f => ({ ...f, avatar_color: c }))}
                style={{ width: 24, height: 24, borderRadius: 6, background: c, cursor: 'pointer', border: form.avatar_color === c ? '2px solid #fff' : '2px solid transparent' }} />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={createTeam} className="btn btn-primary">Create</button>
            <button onClick={() => setCreating(false)} className="btn btn-ghost">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div style={{ color: 'var(--text-3)', padding: 40, textAlign: 'center' }}>Loading…</div> : (
        teams.length === 0
          ? <div style={{ ...S.card, textAlign: 'center', color: 'var(--text-3)', padding: 40 }}>No teams yet. Create one to organise your members.</div>
          : teams.map(t => (
            <div key={t.id} style={S.card} onClick={() => loadTeam(t.id)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={S.avatar(t.avatar_color || '#5865f2')}>{t.name?.[0]}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{t.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{t.member_count || 0} members · {t.manager_name ? `Manager: ${t.manager_name}` : 'No manager'}</div>
                </div>
                <span style={{ color: 'var(--text-3)', fontSize: 14 }}>›</span>
              </div>
            </div>
          ))
      )}
    </div>
  );
}
